import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card.jsx';
import { Button } from './ui/button.jsx';
import { Badge } from './ui/badge.jsx';
import { Input } from './ui/input.jsx';
import { Label } from './ui/label.jsx';
import { Textarea } from './ui/textarea.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog.jsx';

const GestionArchivos = () => {
  const { user, API } = useContext(AuthContext);
  const navigate = useNavigate();

  // Security check - only admin and profesor can access
  if (!user || !['admin', 'profesor'].includes(user.rol)) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="shadow-lg border-red-200">
          <CardContent className="text-center py-12">
            <i className="fas fa-lock text-red-500 text-6xl mb-6"></i>
            <h3 className="text-xl font-semibold text-red-800 mb-2">Acceso Restringido</h3>
            <p className="text-red-600">No tienes permisos para acceder a la gestión de archivos.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  const [archivos, setArchivos] = useState([]);
  const [clases, setClases] = useState([]);
  const [formularios, setFormularios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generando, setGenerando] = useState(false);
  const [eliminando, setEliminando] = useState(null);
  const [filtroClase, setFiltroClase] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [showCrearFormulario, setShowCrearFormulario] = useState(false);
  const [showReporteIndividual, setShowReporteIndividual] = useState(false);
  const [showReporteAsistencias, setShowReporteAsistencias] = useState(false);
  const [showReporteHorasProfesores, setShowReporteHorasProfesores] = useState(false);
  const [showReporteCierreSemestre, setShowReporteCierreSemestre] = useState(false);
  const [historialReportes, setHistorialReportes] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [semestres, setSemestres] = useState([]);
  const [semestreSeleccionado, setSemestreSeleccionado] = useState('');
  const [estudiantesDisponibles, setEstudiantesDisponibles] = useState([]);
  const [profesoresDisponibles, setProfesoresDisponibles] = useState([]);
  const [estudianteSeleccionado, setEstudianteSeleccionado] = useState('');
  const [profesorSeleccionado, setProfesorSeleccionado] = useState('todos');
  const [nuevoFormulario, setNuevoFormulario] = useState({
    titulo: '',
    descripcion: '',
    fecha_limite: ''
  });

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      const [archivosRes, clasesRes, formulariosRes, semestresRes] = await Promise.all([
        axios.get(`${API}/archivos/reportes`),
        axios.get(`${API}/clases`),
        axios.get(`${API}/formularios/independientes`),  // Solo formularios independientes
        axios.get(`${API}/semestres`)
      ]);
      
      setArchivos(archivosRes.data);
      setClases(clasesRes.data);
      setFormularios(formulariosRes.data || []);
      setSemestres(semestresRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const generarReporte = async (tipoReporte, claseId = null) => {
    if (generando) return; // Prevent multiple clicks
    
    setGenerando(true);
    try {
      const params = new URLSearchParams({ tipo_reporte: tipoReporte });
      if (claseId) params.append('clase_id', claseId);
      
      // Download PDF directly
      const response = await axios.post(`${API}/archivos/generar-reporte?${params}`, {}, {
        timeout: 60000,
        responseType: 'blob' // Important: receive as blob
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'reporte.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) filename = filenameMatch[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Reporte generado y descargado: ${filename}`);
      
    } catch (error) {
      console.error('❌ ERROR GENERANDO REPORTE:', error);
      console.error('❌ ERROR DETALLE:', error.response?.data?.detail);
      console.error('❌ ERROR COMPLETO:', JSON.stringify(error.response?.data, null, 2));
      
      const errorMessage = error.response?.data?.detail || 'Error al generar reporte';
      
      // Mostrar alert con el error completo
      alert(`ERROR AL GENERAR REPORTE:\n\n${errorMessage}\n\nStatus: ${error.response?.status}\n\nPor favor copia este mensaje y envialo al desarrollador.`);
      
      toast.error(errorMessage, { 
        duration: 10000,
        description: 'Ver consola del navegador (Ctrl+Shift+I) para más detalles'
      });
    } finally {
      // Always reset generando state after a delay
      setTimeout(() => {
        setGenerando(false);
      }, 1000);
    }
  };

  const cargarEstudiantes = async () => {
    try {
      const response = await axios.get(`${API}/archivos/estudiantes-disponibles`);
      setEstudiantesDisponibles(response.data);
    } catch (error) {
      console.error('Error loading students:', error);
      toast.error('Error al cargar estudiantes');
    }
  };

  const cargarProfesores = async () => {
    try {
      const response = await axios.get(`${API}/archivos/profesores-disponibles`);
      setProfesoresDisponibles(response.data);
    } catch (error) {
      console.error('Error loading professors:', error);
      toast.error('Error al cargar profesores');
    }
  };

  const generarReporteAsistencias = async () => {
    if (!estudianteSeleccionado) {
      toast.error('Por favor selecciona un estudiante');
      return;
    }

    if (generando) return;
    
    setGenerando(true);
    try {
      const response = await axios.post(`${API}/archivos/generar-reporte-asistencias?estudiante_id=${estudianteSeleccionado}`, {}, {
        timeout: 60000,
        responseType: 'blob'
      });
      
      // Download PDF directly
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'reporte_asistencias.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) filename = filenameMatch[1];
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Reporte de asistencias descargado: ${filename}`);
      setShowReporteAsistencias(false);
      setEstudianteSeleccionado('');
      
    } catch (error) {
      console.error('Error generating attendance report:', error);
      toast.error(error.response?.data?.detail || 'Error al generar reporte de asistencias');
    } finally {
      setTimeout(() => {
        setGenerando(false);
      }, 1000);
    }
  };

  const generarReporteHorasProfesores = async () => {
    if (generando) return;
    
    setGenerando(true);
    try {
      // Si es "todos" o vacío, no enviar parámetro
      const url = (profesorSeleccionado && profesorSeleccionado !== 'todos')
        ? `${API}/archivos/generar-reporte-horas-profesores?profesor_id=${profesorSeleccionado}`
        : `${API}/archivos/generar-reporte-horas-profesores`;
      
      const response = await axios.post(url, {}, {
        timeout: 60000,
        responseType: 'blob'
      });
      
      // Download PDF directly
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'reporte_horas_profesores.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) filename = filenameMatch[1];
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`Reporte de horas descargado: ${filename}`);
      setShowReporteHorasProfesores(false);
      setProfesorSeleccionado('');
      
    } catch (error) {
      console.error('Error generating hours report:', error);
      toast.error(error.response?.data?.detail || 'Error al generar reporte de horas');
    } finally {
      setTimeout(() => {
        setGenerando(false);
      }, 1000);
    }
  };

  const generarReporteIndividual = async () => {
    if (!estudianteSeleccionado) {
      toast.error('Por favor selecciona un estudiante');
      return;
    }

    if (generando) return; // Prevent multiple clicks
    
    setGenerando(true);
    try {
      const response = await axios.post(`${API}/archivos/generar-reporte-individual?estudiante_id=${estudianteSeleccionado}`, {}, {
        timeout: 60000,
        responseType: 'blob'
      });
      
      // Download PDF directly
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'reporte_individual.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) filename = filenameMatch[1];
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Reporte individual descargado: ${filename}`);
      setShowReporteIndividual(false);
      setEstudianteSeleccionado('');
      
    } catch (error) {
      console.error('Error generating individual report:', error);
      toast.error(error.response?.data?.detail || 'Error al generar reporte individual');
    } finally {
      setTimeout(() => {
        setGenerando(false);
      }, 1000);
    }
  };

  const enviarReporteIndividualEmail = async () => {
    if (!estudianteSeleccionado) {
      toast.error('Por favor selecciona un estudiante');
      return;
    }
    if (generando || loadingPreview) return;

    // Step 1: Fetch preview from backend and open preview modal
    setLoadingPreview(true);
    try {
      const response = await axios.get(
        `${API}/archivos/reporte-individual-email-preview?estudiante_id=${estudianteSeleccionado}`,
        { timeout: 20000 }
      );
      setEmailPreview({
        ...response.data,
        estudiante_id: estudianteSeleccionado,
      });
      setShowEmailPreview(true);
    } catch (error) {
      console.error('Error loading email preview:', error);
      toast.error(error.response?.data?.detail || 'Error al cargar la vista previa del email');
    } finally {
      setLoadingPreview(false);
    }
  };

  const confirmarEnvioReporteEmail = async () => {
    if (!emailPreview?.estudiante_id) return;
    if (enviandoEmail) return;

    setEnviandoEmail(true);
    setGenerando(true);
    try {
      const response = await axios.post(
        `${API}/archivos/enviar-reporte-individual-email?estudiante_id=${emailPreview.estudiante_id}`,
        {},
        { timeout: 90000 }
      );

      if (response.data?.preview_mode) {
        toast.info(response.data.mensaje || 'Modo Preview: email no enviado realmente');
      } else {
        const provider = response.data?.provider || 'email';
        const destinatario = response.data?.destinatario || emailPreview.destinatario;
        const providerLabel = provider === 'gmail_smtp'
          ? 'Gmail SMTP institucional'
          : provider === 'resend'
            ? 'Resend'
            : provider;
        toast.success(
          `✅ Reporte enviado a ${destinatario} vía ${providerLabel}. Puede tardar unos minutos en llegar — pedile al estudiante que revise su carpeta de Spam / Correo no deseado.`,
          { duration: 8000 }
        );
      }

      // Refresh history
      cargarHistorialReportes();

      // Close both modals
      setShowEmailPreview(false);
      setEmailPreview(null);
      setShowReporteIndividual(false);
      setEstudianteSeleccionado('');
    } catch (error) {
      console.error('Error emailing individual report:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar el reporte por email');
    } finally {
      setEnviandoEmail(false);
      setTimeout(() => {
        setGenerando(false);
      }, 1000);
    }
  };

  const cargarHistorialReportes = async () => {
    setLoadingHistorial(true);
    try {
      const response = await axios.get(`${API}/archivos/historial-reportes-enviados?limit=15`);
      setHistorialReportes(response.data || []);
    } catch (error) {
      console.error('Error loading email history:', error);
      // Non-critical, don't toast
    } finally {
      setLoadingHistorial(false);
    }
  };

  useEffect(() => {
    if (showReporteIndividual) {
      if (estudiantesDisponibles.length === 0) {
        cargarEstudiantes();
      }
      cargarHistorialReportes();
    }
  }, [showReporteIndividual]);

  useEffect(() => {
    if (showReporteAsistencias && estudiantesDisponibles.length === 0) {
      cargarEstudiantes();
    }
  }, [showReporteAsistencias]);

  useEffect(() => {
    if (showReporteHorasProfesores && profesoresDisponibles.length === 0) {
      cargarProfesores();
    }
  }, [showReporteHorasProfesores]);

  const generarReporteCierreSemestre = async () => {
    if (!semestreSeleccionado) {
      toast.error('Por favor selecciona un semestre');
      return;
    }

    setGenerando(true);
    try {
      const response = await axios.post(
        `${API}/archivos/generar-reporte-cierre-semestre`,
        { semestre_id: semestreSeleccionado },
        { 
          timeout: 60000,
          responseType: 'blob'
        }
      );

      // Download PDF directly
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'reporte_cierre_semestre.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) filename = filenameMatch[1];
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Reporte de cierre descargado: ${filename}`);
      setShowReporteCierreSemestre(false);
      setSemestreSeleccionado('');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(error.response?.data?.detail || 'Error al generar reporte');
    } finally {
      setGenerando(false);
    }
  };

  const verArchivo = async (archivoId, filename) => {
    try {
      // Obtener el archivo como blob con autenticación
      const response = await axios.get(`${API}/archivos/ver/${archivoId}`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });
      
      // Determinar el tipo MIME del archivo
      const contentType = response.headers['content-type'] || 'application/pdf';
      
      // Crear blob con el tipo correcto
      const blob = new Blob([response.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Abrir en nueva pestaña
      window.open(blobUrl, '_blank');
      
      // Limpiar URL después de 1 minuto
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60000);
      
      toast.success(`Abriendo: ${filename}`);
      
    } catch (error) {
      console.error('Error opening file:', error);
      toast.error(error.response?.data?.detail || 'Error al abrir archivo');
    }
  };

  const descargarArchivo = async (archivoId, filename) => {
    try {
      const response = await axios.get(`${API}/archivos/descargar/${archivoId}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`Descargando: ${filename}`);
      
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error(error.response?.data?.detail || 'Error al descargar archivo');
    }
  };

  const eliminarArchivo = async (archivoId, filename) => {
    if (!window.confirm(`¿Estás seguro de eliminar el archivo "${filename}"?`)) {
      return;
    }
    
    setEliminando(archivoId);
    try {
      await axios.delete(`${API}/archivos/${archivoId}`);
      toast.success('Archivo eliminado exitosamente');
      loadData();
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar archivo');
    } finally {
      setEliminando(null);
    }
  };

  const formatFecha = (fechaStr) => {
    const fecha = new Date(fechaStr);
    return fecha.toLocaleDateString('es-UY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCrearFormulario = async () => {
    try {
      if (!nuevoFormulario.titulo || !nuevoFormulario.descripcion) {
        toast.error('Por favor completa todos los campos requeridos');
        return;
      }

      const response = await axios.post(`${API}/formularios`, {
        titulo: nuevoFormulario.titulo,
        descripcion: nuevoFormulario.descripcion,
        tipo: 'independiente',
        fecha_limite: nuevoFormulario.fecha_limite || null
      });

      toast.success('Formulario creado exitosamente');
      setShowCrearFormulario(false);
      setNuevoFormulario({ titulo: '', descripcion: '', fecha_limite: '' });
      
      // Redirigir a gestionar formulario para agregar preguntas
      navigate(`/app/formulario/${response.data.id}/gestionar`);
    } catch (error) {
      console.error('Error creando formulario:', error);
      toast.error('Error al crear formulario');
    }
  };

  const handleBorrarFormulario = async (formularioId) => {
    if (!window.confirm('¿Estás seguro de eliminar este formulario? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      await axios.delete(`${API}/formularios/${formularioId}`);
      toast.success('Formulario eliminado exitosamente');
      loadData(); // Recargar lista
    } catch (error) {
      console.error('Error borrando formulario:', error);
      toast.error('Error al eliminar formulario');
    }
  };

  const archivosFiltrados = archivos.filter(archivo => {
    if (filtroClase && archivo.clase_id !== filtroClase) return false;
    if (filtroTipo && filtroTipo !== 'todos' && archivo.tipo_reporte !== filtroTipo) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-blue-600 mb-4"></i>
          <p className="text-gray-600">Cargando gestión de archivos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div className="mb-4 lg:mb-0">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              <i className="fas fa-file-alt mr-3 text-blue-600"></i>
              Gestión de Reportes
            </h1>
            <p className="text-gray-600">
              Genera, gestiona y descarga reportes del sistema en diversos formatos
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            <Badge variant="secondary" className="bg-blue-100 text-blue-800">
              {archivos.length} archivos disponibles
            </Badge>
          </div>
        </div>
      </div>

      {/* Generation Section */}
      <Card className="mb-8 shadow-lg border-l-4 border-l-green-500">
        <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
          <CardTitle className="flex items-center text-green-800">
            <i className="fas fa-plus-circle mr-2 text-xl"></i>
            Generar Nuevos Reportes
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            
            {/* General Report */}
            <div className="bg-white p-4 border rounded-lg hover:shadow-md transition-shadow">
              <div className="text-center">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                  <i className="fas fa-chart-bar text-2xl text-blue-600"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Reporte General</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Todas las calificaciones de tus clases
                </p>
                <Button
                  onClick={() => generarReporte('calificaciones')}
                  disabled={generando}
                  className="instituto-gradient text-white w-full"
                >
                  {generando ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Generando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-file-excel mr-2"></i>
                      Generar
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Class-Specific Report */}
            <div className="bg-white p-4 border rounded-lg hover:shadow-md transition-shadow">
              <div className="text-center">
                <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                  <i className="fas fa-chalkboard-teacher text-2xl text-purple-600"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Reporte por Clase</h3>
                <div className="mb-4">
                  <Select onValueChange={(value) => setFiltroClase(value)}>
                    <SelectTrigger className="text-sm">
                      <SelectValue placeholder="Seleccionar clase..." />
                    </SelectTrigger>
                    <SelectContent>
                      {clases.map(clase => (
                        <SelectItem key={clase.id} value={clase.id}>
                          {clase.titulo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={() => generarReporte('calificaciones', filtroClase)}
                  disabled={generando || !filtroClase}
                  className="bg-purple-600 hover:bg-purple-700 text-white w-full"
                >
                  {generando ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Generando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-file-excel mr-2"></i>
                      Generar
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Individual Student Report - NEW */}
            <div className="bg-white p-4 border rounded-lg hover:shadow-md transition-shadow">
              <div className="text-center">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                  <i className="fas fa-user-graduate text-2xl text-green-600"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Reporte Individual</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Documento Word con calificaciones y promedios
                </p>
                <Button
                  onClick={() => setShowReporteIndividual(true)}
                  disabled={generando}
                  className="bg-green-600 hover:bg-green-700 text-white w-full"
                >
                  <i className="fas fa-file-word mr-2"></i>
                  Generar
                </Button>
              </div>
            </div>

            {/* Attendance Report - NEW */}
            <div className="bg-white p-4 border rounded-lg hover:shadow-md transition-shadow">
              <div className="text-center">
                <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                  <i className="fas fa-calendar-check text-2xl text-orange-600"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Reporte de Asistencias</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Asistencias de estudiante por clase
                </p>
                <Button
                  onClick={() => setShowReporteAsistencias(true)}
                  disabled={generando}
                  className="bg-orange-600 hover:bg-orange-700 text-white w-full"
                >
                  <i className="fas fa-file-excel mr-2"></i>
                  Generar
                </Button>
              </div>
            </div>

            {/* Professor Hours Report - NEW */}
            {user?.rol === 'admin' && (
              <div className="bg-white p-4 border rounded-lg hover:shadow-md transition-shadow">
                <div className="text-center">
                  <div className="bg-teal-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                    <i className="fas fa-clock text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Horas de Profesores</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Registro de marcas horarias
                  </p>
                  <Button
                    onClick={() => setShowReporteHorasProfesores(true)}
                    disabled={generando}
                    className="bg-teal-600 hover:bg-teal-700 text-white w-full"
                  >
                    <i className="fas fa-file-excel mr-2"></i>
                    Generar
                  </Button>
                </div>
              </div>
            )}

            {/* Semester Closing Report - NEW */}
            {user?.rol === 'admin' && (
              <div className="bg-white p-4 border rounded-lg hover:shadow-md transition-shadow">
                <div className="text-center">
                  <div className="bg-indigo-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                    <i className="fas fa-flag-checkered text-2xl text-indigo-600"></i>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Reporte Cierre Semestre</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Clases y promedios por semestre
                  </p>
                  <Button
                    onClick={() => setShowReporteCierreSemestre(true)}
                    disabled={generando}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white w-full"
                  >
                    <i className="fas fa-file-word mr-2"></i>
                    Generar
                  </Button>
                </div>
              </div>
            )}

            {/* Quick Stats */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 border border-blue-200 rounded-lg">
              <div className="text-center">
                <div className="bg-blue-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                  <i className="fas fa-info-circle text-2xl text-white"></i>
                </div>
                <h3 className="font-semibold text-blue-900 mb-2">Información</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Reportes organizados por estudiante</li>
                  <li>• Incluye promedios por estudiante</li>
                  <li>• Formato Excel descargable</li>
                  <li>• Historial de archivos generados</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de archivos eliminada - Los reportes se descargan directamente */}

      {/* Modal Reporte Individual */}
      <Dialog open={showReporteIndividual} onOpenChange={setShowReporteIndividual}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center text-green-800">
              <i className="fas fa-user-graduate mr-2"></i>
              Generar Reporte Individual
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800">
                <i className="fas fa-info-circle mr-2"></i>
                Genera un reporte PDF profesional con el logo del instituto, calificaciones detalladas
                por clase y promedio general del estudiante. Podés descargarlo o enviárselo por email
                directamente desde IBA Connect.
              </p>
            </div>

            <div>
              <Label htmlFor="estudiante-select">Seleccionar Estudiante</Label>
              <Select value={estudianteSeleccionado} onValueChange={setEstudianteSeleccionado}>
                <SelectTrigger id="estudiante-select" className="mt-1">
                  <SelectValue placeholder="Selecciona un estudiante..." />
                </SelectTrigger>
                <SelectContent>
                  {estudiantesDisponibles.map(est => (
                    <SelectItem key={est.id} value={est.id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{est.nombre}</span>
                        <span className="text-xs text-gray-500">{est.email}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <p className="text-xs text-blue-800">
                <i className="fas fa-file-pdf mr-1"></i>
                <strong>Formato:</strong> Documento PDF
              </p>
              <p className="text-xs text-blue-800 mt-1">
                <i className="fas fa-download mr-1"></i>
                <strong>Acciones disponibles:</strong> Descargar el PDF localmente o enviarlo por email al estudiante.
              </p>
            </div>

            <div className="flex flex-col gap-2 pt-4">
              <div className="flex gap-2">
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  onClick={generarReporteIndividual}
                  disabled={generando || !estudianteSeleccionado}
                  data-testid="btn-descargar-reporte-individual"
                >
                  {generando ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Procesando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-download mr-2"></i>
                      Descargar PDF
                    </>
                  )}
                </Button>
                <Button
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={enviarReporteIndividualEmail}
                  disabled={generando || loadingPreview || !estudianteSeleccionado}
                  data-testid="btn-enviar-reporte-individual-email"
                >
                  {loadingPreview ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Cargando vista previa...
                    </>
                  ) : generando ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Procesando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane mr-2"></i>
                      Enviar por Email
                    </>
                  )}
                </Button>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setShowReporteIndividual(false);
                  setEstudianteSeleccionado('');
                }}
                disabled={generando}
                data-testid="btn-cancelar-reporte-individual"
              >
                Cancelar
              </Button>
            </div>

            {/* Historial de envíos por email */}
            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-700">
                  <i className="fas fa-history mr-1"></i>
                  Historial de envíos por email (últimos 15)
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={cargarHistorialReportes}
                  disabled={loadingHistorial}
                  data-testid="btn-refrescar-historial-reportes"
                >
                  <i className={`fas fa-sync-alt ${loadingHistorial ? 'fa-spin' : ''} mr-1`}></i>
                  Refrescar
                </Button>
              </div>
              {loadingHistorial ? (
                <p className="text-xs text-gray-500 py-2">Cargando historial…</p>
              ) : historialReportes.length === 0 ? (
                <p className="text-xs text-gray-500 py-2 italic">Aún no se ha enviado ningún reporte por email.</p>
              ) : (
                <div className="max-h-52 overflow-y-auto border border-gray-100 rounded" data-testid="historial-reportes-lista">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1 font-semibold text-gray-600">Destinatario</th>
                        <th className="text-left px-2 py-1 font-semibold text-gray-600">Fecha</th>
                        <th className="text-left px-2 py-1 font-semibold text-gray-600">Estado</th>
                        <th className="text-left px-2 py-1 font-semibold text-gray-600">Vía</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historialReportes.map(h => {
                        const fecha = h.fecha_envio ? new Date(h.fecha_envio) : null;
                        const fechaStr = fecha ? fecha.toLocaleString('es-UY', { timeZone: 'America/Montevideo', dateStyle: 'short', timeStyle: 'short' }) : '—';
                        const estadoColor = h.estado === 'enviado'
                          ? 'text-green-700 bg-green-50'
                          : h.estado === 'error'
                            ? 'text-red-700 bg-red-50'
                            : 'text-yellow-700 bg-yellow-50';
                        const providerLabel = h.provider === 'gmail_smtp' ? 'Gmail' : h.provider === 'resend' ? 'Resend' : '—';
                        return (
                          <tr key={h.id} className="border-t border-gray-100" title={h.error || ''}>
                            <td className="px-2 py-1 text-gray-800 truncate max-w-[180px]" title={h.destinatario}>{h.destinatario}</td>
                            <td className="px-2 py-1 text-gray-600 whitespace-nowrap">{fechaStr}</td>
                            <td className="px-2 py-1">
                              <span className={`inline-block px-1.5 py-0.5 rounded ${estadoColor}`}>
                                {h.estado === 'enviado' ? '✓ enviado' : h.estado === 'error' ? '✕ error' : h.estado || '—'}
                                {h.aperturas > 0 && ' 👁'}
                              </span>
                            </td>
                            <td className="px-2 py-1 text-gray-600">{providerLabel}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-[11px] text-gray-500 mt-1 italic">
                <i className="fas fa-info-circle mr-1"></i>
                "Enviado" = el proveedor aceptó el email. La entrega final depende del servidor del destinatario (Hotmail/Outlook a veces filtran a Spam).
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Preview del Email antes de enviar */}
      <Dialog open={showEmailPreview} onOpenChange={(open) => { if (!enviandoEmail) { setShowEmailPreview(open); if (!open) setEmailPreview(null); } }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="modal-preview-email">
          <DialogHeader>
            <DialogTitle className="flex items-center text-blue-800">
              <i className="fas fa-eye mr-2"></i>
              Vista previa del email — ¿confirmás el envío?
            </DialogTitle>
          </DialogHeader>

          {emailPreview && (
            <div className="space-y-3">
              {/* Meta info */}
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm space-y-1">
                <div><span className="font-semibold text-blue-900">Para:</span> <span className="text-gray-800" data-testid="preview-destinatario">{emailPreview.destinatario || <em className="text-red-600">Sin email registrado</em>}</span></div>
                <div><span className="font-semibold text-blue-900">Estudiante:</span> <span className="text-gray-800">{emailPreview.nombre_estudiante}</span></div>
                <div><span className="font-semibold text-blue-900">Asunto:</span> <span className="text-gray-800" data-testid="preview-asunto">{emailPreview.asunto}</span></div>
                <div><span className="font-semibold text-blue-900">Adjunto:</span> <span className="text-gray-800">PDF con las calificaciones y promedio del estudiante</span></div>
              </div>

              {/* HTML body preview */}
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1">Cuerpo del mensaje:</p>
                <div
                  className="border border-gray-300 rounded overflow-hidden bg-white"
                  data-testid="preview-cuerpo-html"
                  style={{ maxHeight: '360px', overflowY: 'auto' }}
                  dangerouslySetInnerHTML={{ __html: emailPreview.cuerpo_html || '' }}
                />
              </div>

              {/* Warning */}
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
                <i className="fas fa-exclamation-triangle mr-1"></i>
                Revisá el texto (especialmente la firma institucional) antes de confirmar. Este mensaje se enviará al estudiante junto con el PDF adjunto.
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setShowEmailPreview(false); setEmailPreview(null); }}
                  disabled={enviandoEmail}
                  data-testid="btn-cancelar-preview-email"
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={confirmarEnvioReporteEmail}
                  disabled={enviandoEmail || !emailPreview.destinatario}
                  data-testid="btn-confirmar-envio-email"
                >
                  {enviandoEmail ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Enviando…
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane mr-2"></i>
                      Confirmar y Enviar
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Reporte Asistencias */}
      <Dialog open={showReporteAsistencias} onOpenChange={setShowReporteAsistencias}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-orange-800">
              <i className="fas fa-calendar-check mr-2"></i>
              Generar Reporte de Asistencias
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-sm text-orange-800">
                <i className="fas fa-info-circle mr-2"></i>
                Este reporte muestra todas las asistencias registradas del estudiante, 
                incluyendo clases, fechas, estado (Presente/Ausente) y estadísticas.
              </p>
            </div>

            <div>
              <Label htmlFor="estudiante-asistencias-select">Seleccionar Estudiante</Label>
              <Select value={estudianteSeleccionado} onValueChange={setEstudianteSeleccionado}>
                <SelectTrigger id="estudiante-asistencias-select" className="mt-1">
                  <SelectValue placeholder="Selecciona un estudiante..." />
                </SelectTrigger>
                <SelectContent>
                  {estudiantesDisponibles.map(est => (
                    <SelectItem key={est.id} value={est.id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{est.nombre}</span>
                        <span className="text-xs text-gray-500">{est.email}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <p className="text-xs text-blue-800">
                <i className="fas fa-file-excel mr-1"></i>
                <strong>Formato:</strong> Archivo Excel (.xlsx)
              </p>
              <p className="text-xs text-blue-800 mt-1">
                <i className="fas fa-chart-pie mr-1"></i>
                <strong>Incluye:</strong> Estadísticas y porcentaje de asistencia
              </p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowReporteAsistencias(false);
                  setEstudianteSeleccionado('');
                }}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                onClick={generarReporteAsistencias}
                disabled={generando || !estudianteSeleccionado}
              >
                {generando ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Generando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-file-excel mr-2"></i>
                    Generar Reporte
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Reporte Horas Profesores */}
      <Dialog open={showReporteHorasProfesores} onOpenChange={setShowReporteHorasProfesores}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-teal-800">
              <i className="fas fa-clock mr-2"></i>
              Generar Reporte de Horas de Profesores
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
              <p className="text-sm text-teal-800">
                <i className="fas fa-info-circle mr-2"></i>
                Este reporte muestra el registro de horas trabajadas por los profesores, 
                incluyendo fechas, horas de inicio y fin, y total de horas.
              </p>
            </div>

            <div>
              <Label htmlFor="profesor-select">Seleccionar Profesor (Opcional)</Label>
              <Select value={profesorSeleccionado} onValueChange={setProfesorSeleccionado}>
                <SelectTrigger id="profesor-select" className="mt-1">
                  <SelectValue placeholder="Todos los profesores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los profesores</SelectItem>
                  {profesoresDisponibles.map(prof => (
                    <SelectItem key={prof.id} value={prof.id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{prof.nombre}</span>
                        <span className="text-xs text-gray-500">{prof.email}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Deja en blanco para generar reporte de todos los profesores
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <p className="text-xs text-blue-800">
                <i className="fas fa-file-excel mr-1"></i>
                <strong>Formato:</strong> Archivo Excel (.xlsx)
              </p>
              <p className="text-xs text-blue-800 mt-1">
                <i className="fas fa-calculator mr-1"></i>
                <strong>Incluye:</strong> Total de horas trabajadas
              </p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowReporteHorasProfesores(false);
                  setProfesorSeleccionado('todos');
                }}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                onClick={generarReporteHorasProfesores}
                disabled={generando}
              >
                {generando ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Generando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-file-excel mr-2"></i>
                    Generar Reporte
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal - Reporte Cierre Semestre */}
      <Dialog open={showReporteCierreSemestre} onOpenChange={setShowReporteCierreSemestre}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-2xl">
              <i className="fas fa-flag-checkered text-indigo-600 mr-3"></i>
              Reporte Cierre de Semestre
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <Label className="text-sm font-semibold text-indigo-900 mb-2 block">
                <i className="fas fa-graduation-cap mr-2"></i>
                Selecciona el Semestre
              </Label>
              <Select value={semestreSeleccionado} onValueChange={setSemestreSeleccionado}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Selecciona un semestre..." />
                </SelectTrigger>
                <SelectContent>
                  {semestres.map((semestre) => (
                    <SelectItem key={semestre.id} value={semestre.id}>
                      {semestre.name || semestre.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-indigo-600 mt-2">
                El reporte incluirá todas las clases del semestre con sus estudiantes y promedios
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-xs text-yellow-800 flex items-start">
                <i className="fas fa-info-circle mt-0.5 mr-2"></i>
                <span>
                  Los promedios <strong>menores o iguales a 69</strong> aparecerán resaltados en color rojo (No Aprobado)
                </span>
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowReporteCierreSemestre(false);
                  setSemestreSeleccionado('');
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={generarReporteCierreSemestre}
                disabled={generando || !semestreSeleccionado}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {generando ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Generando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-file-word mr-2"></i>
                    Generar Reporte
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionArchivos;